/* =====================================================================
 * Active tenant registry (Phase 11-4b-tenant).
 *
 * Holds the tenant manifest that the HOST app resolved at startup so
 * shared packages (e.g. `@auw/workspace-utils`) can derive storage
 * keys and colon-namespaces without re-reading the manifest or
 * depending on a React context.
 *
 * Lifecycle:
 *   - Host app's module-scope initialization code calls
 *     `setActiveTenantManifest(manifest)` exactly once, before any
 *     module-level code in a shared package reads the registry.
 *     (askyourai does this from `src/tenant/storage.ts`; enterprise
 *     from `apps/enterprise/src/tenants/index.ts`.)
 *   - Shared code calls `activeTenantId()`, `activeTenantKey()`,
 *     `activeTenantNamespacedKey()` lazily.
 *
 * The registry is intentionally minimal: no React context, no async,
 * no subscriptions. Tenant identity is fixed for the life of a page
 * load — changing tenants requires a full reload.
 * ===================================================================== */

import type { TenantManifest } from './types';
import { tenantStoragePrefix } from './storage';

let _active: TenantManifest | null = null;

/**
 * Register the tenant manifest for the current app process.
 *
 * MUST be called by the host bootstrap code before any shared module
 * imports read the registry. Calling twice with the same manifest is
 * a no-op; calling with a different manifest throws (would indicate
 * a mistaken dual-bootstrap).
 */
export function setActiveTenantManifest(manifest: TenantManifest): void {
  if (_active && _active.id !== manifest.id) {
    throw new Error(
      `[auw/tenant] setActiveTenantManifest() called twice with different tenants ` +
        `('${_active.id}' then '${manifest.id}'). The tenant registry is immutable ` +
        `after first registration.`,
    );
  }
  _active = manifest;
}

/**
 * Return the previously registered manifest.
 *
 * Throws if the host never registered one. The error message points
 * callers to the host-side fix rather than silently returning a
 * default, because a missing registration almost always indicates a
 * lazy-evaluation bug (a shared store evaluated before the host's
 * bootstrap ran).
 */
export function getActiveTenantManifest(): TenantManifest {
  if (!_active) {
    throw new Error(
      '[auw/tenant] Active tenant manifest has not been set. ' +
        'The host app must call setActiveTenantManifest(manifest) during bootstrap ' +
        'before any shared-package module reads tenant-scoped keys.',
    );
  }
  return _active;
}

/**
 * Active tenant id (e.g. `'askyourai'`, `'samsung'`).
 */
export function activeTenantId(): string {
  return getActiveTenantManifest().id;
}

/**
 * Tenant-scoped storage-key prefix including trailing underscore.
 * Equivalent to `tenantStoragePrefix(getActiveTenantManifest())`.
 *
 *   activeTenantPrefix()             // 'askyourai_'
 *   activeTenantKey('user_cache')    // 'askyourai_user_cache'
 */
export function activeTenantPrefix(): string {
  return tenantStoragePrefix(getActiveTenantManifest());
}

/**
 * Compose a full `${prefix}${suffix}` storage key.
 */
export function activeTenantKey(suffix: string): string {
  return `${activeTenantPrefix()}${suffix}`;
}

/**
 * Compose a colon-delimited namespace key used by subsystems that
 * historically preferred `${id}:foo:bar` over `${id}_foo_bar`
 * (widget modals, plugin OAuth popup names, broadcast-channel IDs).
 *
 *   activeTenantNamespacedKey('widget', 'calendar-modal')
 *     // 'askyourai:widget:calendar-modal'
 */
export function activeTenantNamespacedKey(...parts: readonly string[]): string {
  return [activeTenantId(), ...parts].join(':');
}

/**
 * Brand-facing product name for the active tenant. Falls back to
 * `'AUW'` if no manifest is registered yet (should never occur in
 * production — hosts register before first render).
 *
 * Used by chat preview titles, share URLs, OG tags and similar copy
 * where the product name must respect the tenant branding but the
 * caller does not want to pay for a full React re-render every time
 * the manifest changes (tenant swaps are not supported at runtime).
 */
export function activeTenantAppName(): string {
  return getActiveTenantManifest()?.identity?.appName ?? 'AUW';
}
